import { docEntityBaseNew } from "building-types/entities/docEntityBaseNew";
import type { FoundationComponentEntity } from "building-types/foundations/foundationComponentEntityTypes";
import {
  type StripFootingEntity,
  type TurndownFootingEntity,
  type CircularIsolatedFootingEntity,
  type RectangularIsolatedFootingEntity,
  type CIPStemWallEntity,
  type CMUStemWallEntity,
  type FixedWidthCurbEntity,
  type AutoWidthCurbEntity,
  type CircularCMUPierEntity,
  type RectangularCMUPierEntity,
  type CircularCIPPierEntity,
  type RectangularCIPPierEntity,
  type CustomPierEntity,
} from "building-types/foundations/foundationComponentEntityTypes";
import { FoundationComponentType } from "building-types/foundations/foundationComponentEntityTypes";
import type { BrickLedgeBehavior, BrickLedgeType } from "building-types/foundations/foundationEnums";
import { FootingType, PierType, StemWallType, CurbType } from "building-types/foundations/foundationEnums";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import uid from "common/utils/uid";
import type { PolyLine2 } from "geom/polyLine2Types";

export function foundationComponentCopy(component: FoundationComponentEntity, name: string): FoundationComponentEntity {
  return {
    ...component,
    name,
  };
}

export function foundationComponentNew(name: string, component: FoundationComponentEntity): FoundationComponentEntity {
  return {
    ...component,
    name,
  };
}

// Footing factory functions

export function stripFootingNew(
  name: string,
  footingWidth: number,
  footingDepth: number,
  footingPadding: number,
  id: string = uid(),
  sortIndex: number = 0,
): StripFootingEntity {
  return {
    ...docEntityBaseNew("FOUNDATION_COMPONENT", id),
    sortIndex,
    componentType: FoundationComponentType.FOOTING,
    footingType: FootingType.STRIP,
    name,
    footingWidth,
    footingDepth,
    footingPadding,
  };
}

export function turndownFootingNew(
  name: string,
  footingWidth: number,
  brickLedgeBehavior: BrickLedgeBehavior,
  brickLedgeType: BrickLedgeType.ADD | BrickLedgeType.NONE,
  brickLedgeWidth: number,
  id: string = uid(),
  sortIndex: number = 0,
): TurndownFootingEntity {
  return {
    ...docEntityBaseNew("FOUNDATION_COMPONENT", id),
    sortIndex,
    componentType: FoundationComponentType.FOOTING,
    footingType: FootingType.TURNDOWN,
    name,
    footingWidth,
    brickLedgeBehavior,
    brickLedgeType,
    brickLedgeWidth,
  };
}

export function circularIsolatedFootingNew(
  name: string,
  footingRadius: number,
  footingDepth: number,
  material?: ExteriorMaterial,
  id: string = uid(),
  sortIndex: number = 0,
): CircularIsolatedFootingEntity {
  return {
    ...docEntityBaseNew("FOUNDATION_COMPONENT", id),
    sortIndex,
    componentType: FoundationComponentType.FOOTING,
    footingType: FootingType.CIRCULAR_ISOLATED,
    name,
    footingRadius,
    footingDepth,
    material,
  };
}

export function rectangularIsolatedFootingNew(
  name: string,
  footingLength: number,
  footingWidth: number,
  footingDepth: number,
  material?: ExteriorMaterial,
  id: string = uid(),
  sortIndex: number = 0,
): RectangularIsolatedFootingEntity {
  return {
    ...docEntityBaseNew("FOUNDATION_COMPONENT", id),
    sortIndex,
    componentType: FoundationComponentType.FOOTING,
    footingType: FootingType.RECTANGULAR_ISOLATED,
    name,
    footingLength,
    footingWidth,
    footingDepth,
    material,
  };
}

// Stem Wall factory functions

export function cipStemWallNew(
  name: string,
  stemWallWidth: number,
  brickLedgeBehavior: BrickLedgeBehavior,
  brickLedgeType: BrickLedgeType,
  brickLedgeWidth: number,
  id: string = uid(),
  sortIndex: number = 0,
): CIPStemWallEntity {
  return {
    ...docEntityBaseNew("FOUNDATION_COMPONENT", id),
    sortIndex,
    componentType: FoundationComponentType.STEMWALL,
    stemWallType: StemWallType.CIP,
    name,
    stemWallWidth,
    brickLedgeBehavior,
    brickLedgeType,
    brickLedgeWidth,
  };
}

export function cmuStemWallNew(
  name: string,
  stemWallBlockWidth: number,
  brickLedgeBehavior: BrickLedgeBehavior,
  brickLedgeType: BrickLedgeType.ADD | BrickLedgeType.NONE,
  stemWallBrickLedgeBlockWidth: number,
  id: string = uid(),
  sortIndex: number = 0,
): CMUStemWallEntity {
  return {
    ...docEntityBaseNew("FOUNDATION_COMPONENT", id),
    sortIndex,
    componentType: FoundationComponentType.STEMWALL,
    stemWallType: StemWallType.CMU,
    name,
    stemWallBlockWidth,
    brickLedgeBehavior,
    brickLedgeType,
    stemWallBrickLedgeBlockWidth,
  };
}

// Curb factory functions

export function fixedWidthCurbNew(
  name: string,
  curbWidth: number,
  id: string = uid(),
  sortIndex: number = 0,
): FixedWidthCurbEntity {
  return {
    ...docEntityBaseNew("FOUNDATION_COMPONENT", id),
    sortIndex,
    componentType: FoundationComponentType.CURB,
    curbType: CurbType.FIXED_WIDTH,
    name,
    curbWidth,
  };
}

export function autoWidthCurbNew(name: string, id: string = uid(), sortIndex: number = 0): AutoWidthCurbEntity {
  return {
    ...docEntityBaseNew("FOUNDATION_COMPONENT", id),
    sortIndex,
    componentType: FoundationComponentType.CURB,
    curbType: CurbType.AUTO_WIDTH,
    name,
  };
}

// Pier factory functions

export function circularCMUPierNew(
  name: string,
  pierRadius: number,
  pierDepth?: number,
  id: string = uid(),
  sortIndex: number = 0,
): CircularCMUPierEntity {
  return {
    ...docEntityBaseNew("FOUNDATION_COMPONENT", id),
    sortIndex,
    componentType: FoundationComponentType.PIER,
    pierType: PierType.CIRCULAR_CMU,
    name,
    pierRadius,
    pierDepth,
  };
}

export function rectangularCMUPierNew(
  name: string,
  pierLength: number,
  pierWidth: number,
  pierDepth?: number,
  id: string = uid(),
  sortIndex: number = 0,
): RectangularCMUPierEntity {
  return {
    ...docEntityBaseNew("FOUNDATION_COMPONENT", id),
    sortIndex,
    componentType: FoundationComponentType.PIER,
    pierType: PierType.RECTANGULAR_CMU,
    name,
    pierLength,
    pierWidth,
    pierDepth,
  };
}

export function circularCIPPierNew(
  name: string,
  pierRadius: number,
  pierDepth?: number,
  material?: ExteriorMaterial,
  id: string = uid(),
  sortIndex: number = 0,
): CircularCIPPierEntity {
  return {
    ...docEntityBaseNew("FOUNDATION_COMPONENT", id),
    sortIndex,
    componentType: FoundationComponentType.PIER,
    pierType: PierType.CIRCULAR_CIP,
    name,
    pierRadius,
    pierDepth,
    material,
  };
}

export function rectangularCIPPierNew(
  name: string,
  pierLength: number,
  pierWidth: number,
  pierDepth?: number,
  material?: ExteriorMaterial,
  id: string = uid(),
  sortIndex: number = 0,
): RectangularCIPPierEntity {
  return {
    ...docEntityBaseNew("FOUNDATION_COMPONENT", id),
    sortIndex,
    componentType: FoundationComponentType.PIER,
    pierType: PierType.RECTANGULAR_CIP,
    name,
    pierLength,
    pierWidth,
    pierDepth,
    material,
  };
}

export function customPierNew(
  name: string,
  pierPoints: PolyLine2,
  id: string = uid(),
  sortIndex: number = 0,
): CustomPierEntity {
  return {
    ...docEntityBaseNew("FOUNDATION_COMPONENT", id),
    sortIndex,
    componentType: FoundationComponentType.PIER,
    pierType: PierType.CUSTOM,
    name,
    pierPoints,
  };
}

export function isIsolatedFootingEntity(
  component: FoundationComponentEntity,
): component is CircularIsolatedFootingEntity | RectangularIsolatedFootingEntity {
  return (
    component.componentType === FoundationComponentType.FOOTING &&
    (component.footingType === FootingType.CIRCULAR_ISOLATED ||
      component.footingType === FootingType.RECTANGULAR_ISOLATED)
  );
}

export function isStripFootingEntity(component: FoundationComponentEntity): component is StripFootingEntity {
  return component.componentType === FoundationComponentType.FOOTING && component.footingType === FootingType.STRIP;
}

export function isTurndownFootingEntity(component: FoundationComponentEntity): component is TurndownFootingEntity {
  return component.componentType === FoundationComponentType.FOOTING && component.footingType === FootingType.TURNDOWN;
}

export function isCMUPierEntity(component: FoundationComponentEntity): component is CircularCMUPierEntity {
  return (
    component.componentType === FoundationComponentType.PIER &&
    (component.pierType === PierType.CIRCULAR_CMU || component.pierType === PierType.RECTANGULAR_CMU)
  );
}

export function isFootingEntity(
  component: FoundationComponentEntity,
): component is
  | StripFootingEntity
  | TurndownFootingEntity
  | CircularIsolatedFootingEntity
  | RectangularIsolatedFootingEntity {
  return component.componentType === FoundationComponentType.FOOTING;
}

export function isStemWallEntity(
  component: FoundationComponentEntity,
): component is CIPStemWallEntity | CMUStemWallEntity {
  return component.componentType === FoundationComponentType.STEMWALL;
}

export function isCIPStemWallEntity(component: FoundationComponentEntity): component is CIPStemWallEntity {
  return component.componentType === FoundationComponentType.STEMWALL && component.stemWallType === StemWallType.CIP;
}

export function isCMUStemWallEntity(component: FoundationComponentEntity): component is CMUStemWallEntity {
  return component.componentType === FoundationComponentType.STEMWALL && component.stemWallType === StemWallType.CMU;
}

export function isCurbEntity(
  component: FoundationComponentEntity,
): component is FixedWidthCurbEntity | AutoWidthCurbEntity {
  return component.componentType === FoundationComponentType.CURB;
}

export function isPierEntity(
  component: FoundationComponentEntity,
): component is
  | CircularCMUPierEntity
  | RectangularCMUPierEntity
  | CircularCIPPierEntity
  | RectangularCIPPierEntity
  | CustomPierEntity {
  return component.componentType === FoundationComponentType.PIER;
}
